/**
 * Shared cell + badge primitives used by MembersTable, columns.tsx, and PrintView.
 *
 * Visual language:
 *   - Badges are no-border, _.100 bg + _.700 text (matches the "Read only" chip
 *     in `store-settings/components/section.tsx`'s `Card`).
 *   - Row labels use `size="md" fontWeight={500} color="gray.900"` for primary
 *     (name) values and `size="md" color="gray.700"` for secondary cells.
 *   - Header cells use the small-uppercase-label treatment:
 *     `size="2xs" fontWeight={600} color="gray.500" letterSpacing={0.4}`.
 */
import React from 'react';
import { Box, HStack } from 'native-base';
import { Text } from '@pimm/base';
import type { DriftKind, ImportSyncMode, TeamMember, TeamMemberRole, TeamMemberStatus, UserLoginStatus } from '../types';
import { RECOVERY_EMAIL_STATUS_VERIFIED } from '../types';

// --- date helpers ---
export const formatDateShort = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Birthday is year-less throughout the app (month/day only, matching the
// masked edit input) — parses the API's real ISO datetime value (previously
// this incorrectly assumed a bare "MM-DD" string) but never displays the
// year. UTC getters avoid a local-timezone day-shift on the API's
// UTC-suffixed value (same reasoning as utils.ts's fromIsoMonthDay).
export const formatBirthday = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

// Site-local calendar midnight — used to diff whole days crossed rather than
// elapsed 24-hour windows, so e.g. a 6pm login checked at 9am the next day
// reads as "Yesterday" instead of "Today" (Math.round guards against 23/25
// hour DST days landing off the exact integer). `targetOffset` (from
// @pimm/common's core/date.ts, registered globally at app bootstrap) shifts
// the instant so its local getters read the site's wall-clock time — same
// primitive useSiteTime().today() is built on — so "today" tracks the
// store's clock, not the viewing device's.
const startOfDay = (date: Date, utcOffsetMinutes: number): number => {
  const shifted = date.targetOffset(utcOffsetMinutes);
  return new Date(shifted.getFullYear(), shifted.getMonth(), shifted.getDate()).getTime();
};

export const formatRelativeDays = (
  iso: string | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
  utcOffsetMinutes: number,
): string => {
  if (!iso) return t('store_settings:relative_never');
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffDays = Math.round((startOfDay(new Date(), utcOffsetMinutes) - startOfDay(d, utcOffsetMinutes)) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return t('store_settings:relative_today');
  if (diffDays === 1) return t('store_settings:relative_yesterday');
  if (diffDays < 7) return t('store_settings:relative_days_ago', { count: diffDays });
  if (diffDays < 30) return t('store_settings:relative_weeks_ago', { count: Math.floor(diffDays / 7) });
  if (diffDays < 365) return t('store_settings:relative_months_ago', { count: Math.floor(diffDays / 30) });
  return t('store_settings:relative_years_ago', { count: Math.floor(diffDays / 365) });
};

// --- badge style maps ---
// Recipe: _.100 bg + _.700 text. No borders.
// Color scheme: Active → green (employed, working); Inactive → gray (paused but
// recoverable); Terminated → red (gone — distinctly bad). Matches the Employment
// Status icon tints used in the Users view (UserRoundCheck/X icons).
export const statusBadgeStyle = (status: TeamMemberStatus) => {
  switch (status) {
    case 'Active':
      return { bg: 'success.100', text: 'success.700' };
    case 'Inactive':
      return { bg: 'gray.100', text: 'gray.700' };
    case 'Terminated':
    default:
      return { bg: 'error.100', text: 'error.700' };
  }
};

export const roleBadgeStyle = (role: TeamMemberRole) => {
  switch (role) {
    case 'SMS MANAGER':
      return { bg: 'primary.100', text: 'primary.700' };
    case 'SMS USER':
      return { bg: 'blue.100', text: 'blue.700' };
    case 'SMS EMPLOYEE':
    default:
      return { bg: 'gray.100', text: 'gray.600' };
  }
};

// Auto Sync mode icon tint — three states:
//   auto      → blue.500   ("On" — healthy default, always shown)
//   protected → amber.500  ("Status/Role Only" — partial freeze)
//   locked    → error.500  ("Off" — full freeze)
// All three states render the same `refresh-cw` icon so the column position is
// consistent across every row. Blue = all good; amber/red = needs attention.
// NOTE: opsbee `primary` = brand golden yellow, NOT blue — use the explicit
// `blue` palette for any "healthy/on/active" icon tint.
export const importSyncIconColor = (mode: ImportSyncMode): string => {
  if (mode === 'Locked') return 'error.500';
  if (mode === 'Protected') return 'amber.500';
  return 'blue.500';
};

// User login status (Pending / Active / Disabled).
// Active=green, Pending=gray (invited, nothing actionable yet), Disabled=red
// (login blocked — distinctly bad).
export const userStatusBadgeStyle = (status: UserLoginStatus) => {
  switch (status) {
    case 'Active':
      return { bg: 'success.100', text: 'success.700' };
    case 'Pending':
      return { bg: 'gray.100', text: 'gray.700' };
    case 'Disabled':
    default:
      return { bg: 'error.100', text: 'error.700' };
  }
};

// Importer Sync mode badge — mirrors importSyncIconColor's tint choice
// (blue=on/healthy, amber=partial freeze, red=full freeze) in the same
// _.100/_.700 Badge recipe used by the other badge style maps.
export const syncModeBadgeStyle = (mode: ImportSyncMode) => {
  switch (mode) {
    case 'Locked':
      return { bg: 'error.100', text: 'error.700' };
    case 'Protected':
      return { bg: 'amber.100', text: 'amber.800' };
    case 'Auto':
    default:
      return { bg: 'blue.100', text: 'blue.700' };
  }
};

// --- drift detection ---
// These predicates surface employees whose Employee record disagrees with their
// User record, or whose lifecycle state suggests a sync issue. Pure client-side
// derivation over fields the merged GET /api/Workforce/TeamMembers response
// already returns — there is no separate drift-detection endpoint.

const DRIFT_THRESHOLD_DAYS = 30;

// Calendar-day diff (see startOfDay above) — a record dated 11:50pm checked
// at 12:10am the next day is 1 day ago, not 0, even though only 20 elapsed
// minutes separate them.
const daysAgo = (iso: string | null | undefined, utcOffsetMinutes: number): number | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.round((startOfDay(new Date(), utcOffsetMinutes) - startOfDay(d, utcOffsetMinutes)) / (1000 * 60 * 60 * 24));
};

// Status mismatch — Employee and User records disagree on lifecycle. Sync
// rule: terminated/inactive employee → user should be Disabled; active
// employee → user should be Active. Pending users don't count as drift
// (invited-but-not-activated is a valid transient state).
export const hasStatusMismatch = (m: TeamMember): boolean => {
  if (!m.user) return false;
  if (m.user.status === 'Pending') return false;
  // Demoted-with-kept-login exception — role SMS EMPLOYEE with a linked user
  // is the rare post-Demote state (login disabled, not deleted, per Demote's
  // own documented behavior). An Active employee + Disabled login is CORRECT
  // here, not a sync failure — the login is intentionally dead weight until/
  // unless the employee is promoted again. Don't flag something that isn't broken.
  if (m.role === 'SMS EMPLOYEE') return false;
  if (m.status === 'Active') return m.user.status !== 'Active';
  return m.user.status === 'Active';
};

// Role mismatch — Employee.role differs from the role on the User record.
// Only Active employees are actionable — Inactive/Terminated employees aren't
// currently working, so their role drift isn't something a manager needs to
// act on right now; only a currently-Active employee's role/user-role
// disagreement is worth surfacing.
export const hasRoleMismatch = (m: TeamMember): boolean => {
  if (!m.user) return false;
  if (m.status !== 'Active') return false;
  return m.role !== m.user.userRole;
};

// Stale schedule — active employee not scheduled in > 30 days. Diagnostic
// signal that back-office employeeId mapping may be broken (or the person is
// no longer working but back-office hasn't terminated them yet).
export const hasStaleSchedule = (m: TeamMember, utcOffsetMinutes: number): boolean => {
  if (m.status !== 'Active') return false;
  const days = daysAgo(m.lastScheduledDateUtc, utcOffsetMinutes);
  if (days == null) return true; // never scheduled = stale
  return days > DRIFT_THRESHOLD_DAYS;
};

// Stale login — Active user (login record) hasn't logged in in > 30 days, or
// has never logged in at all. Same 30-day threshold as Stale Schedule. Gated
// on User.status === 'Active' (Pending users haven't been expected to log in
// yet; Disabled users can't, so login can't be "stale"). Shared by Team view
// (informative cross-check) and Users view (primary login-health signal).
export const hasStaleLogin = (m: TeamMember, utcOffsetMinutes: number): boolean => {
  if (!m.user) return false;
  if (m.user.status !== 'Active') return false;
  const days = daysAgo(m.user.lastLoginDateUtc, utcOffsetMinutes);
  if (days == null) return true; // never logged in = stale
  return days > DRIFT_THRESHOLD_DAYS;
};

// Overdue purge — terminated employee whose StatusDate is > 30 days old AND
// importSyncMode !== 'Locked'. These should have been deleted by the backend
// PurgeTerminated sweep. The Team Roster handoff doc (ClickUp doc 12f9mz-52571,
// PR 1) confirms PurgeTerminated's only caller is currently commented out
// server-side, so non-zero rows here are expected until that's fixed (tracked
// separately with Shah) — not a bug in this branch.
// Locked records are intentionally excluded — they're frozen by manager intent.
export const hasOverduePurge = (m: TeamMember, utcOffsetMinutes: number): boolean => {
  if (m.status !== 'Terminated') return false;
  if (m.importSyncMode === 'Locked') return false;
  const days = daysAgo(m.statusDateUtc, utcOffsetMinutes);
  if (days == null) return false;
  return days > DRIFT_THRESHOLD_DAYS;
};

// ─── Users-view drift (login health) ─────────────────────────────────────
// User exists but no PIN set — manager can't authenticate at POS.
export const hasNoPinSet = (m: TeamMember): boolean => {
  if (!m.user) return false;
  return !m.user.pinSet;
};

// User has a login but no User.email — manager has no way to be emailed about
// product updates, support tickets, etc. Fully independent of Employee.email
// (a separate Team-view field).
export const hasNoContactEmail = (m: TeamMember): boolean => {
  if (!m.user) return false;
  return !m.user.email;
};

// User has a login but no *verified* recovery email — password reset path is
// broken. A value that is missing, pending, or expired all count as "no
// recovery email." Distinct from noContactEmail (product comms).
export const hasNoRecoveryEmail = (m: TeamMember): boolean => {
  if (!m.user) return false;
  return !(m.user.recoveryEmail && m.user.recoveryEmailStatus === RECOVERY_EMAIL_STATUS_VERIFIED);
};

export const matchesDrift = (m: TeamMember, kind: DriftKind, utcOffsetMinutes: number): boolean => {
  switch (kind) {
    case 'statusMismatch':
      return hasStatusMismatch(m);
    case 'roleMismatch':
      return hasRoleMismatch(m);
    case 'staleSchedule':
      return hasStaleSchedule(m, utcOffsetMinutes);
    case 'staleLogin':
      return hasStaleLogin(m, utcOffsetMinutes);
    case 'overduePurge':
      return hasOverduePurge(m, utcOffsetMinutes);
    case 'noPinSet':
      return hasNoPinSet(m);
    case 'noContactEmail':
      return hasNoContactEmail(m);
    case 'noRecoveryEmail':
      return hasNoRecoveryEmail(m);
  }
};

// --- primitives ---
export type CellProps = { children: React.ReactNode; flex?: number; w?: number; align?: 'left' | 'right' };

export const Cell: React.FC<CellProps> = ({ children, flex, w, align = 'left' }) => (
  <Box
    flex={w == null ? flex ?? 1 : undefined}
    w={w}
    px={4}
    py={3}
    justifyContent="center"
    alignItems={align === 'right' ? 'flex-end' : 'flex-start'}
  >
    {children}
  </Box>
);

export const HeaderCell: React.FC<{ children: React.ReactNode; flex?: number; w?: number }> = ({ children, flex, w }) => (
  <Box flex={w == null ? flex ?? 1 : undefined} w={w} px={4} py={2.5} justifyContent="center">
    <Text size="2xs" fontWeight={600} color="gray.500" letterSpacing={0.4}>
      {(children as string).toUpperCase()}
    </Text>
  </Box>
);

// Generic badge — _.100 bg + _.700 text, no border. `LeadingIcon` is optional
// (used by the Employee Profile modal for icon-in-pill badges) — every
// existing call site omits it, so this is behavior-preserving. `size`
// defaults to 'xs' to match the dense grid rows this was built for; the
// Employee Profile modal's badges pass size="sm" explicitly to match the
// surrounding field text.
export const Badge: React.FC<{
  bg: string;
  text: string;
  label: string;
  LeadingIcon?: React.ComponentType<{ size?: number; color?: string }>;
  size?: 'xs' | 'sm';
}> = ({ bg, text, label, LeadingIcon, size = 'xs' }) => (
  <Box bg={bg} rounded="md" px={2.5} py={1}>
    <HStack space={1} alignItems="center">
      {!!LeadingIcon && <LeadingIcon size={size === 'sm' ? 4 : 3} color={text} />}
      <Text size={size} fontWeight={600} color={text}>
        {label}
      </Text>
    </HStack>
  </Box>
);

// Yes/No pill for boolean fields. Yes = success palette; No = neutral gray.
export const YesNoBadge: React.FC<{ value: boolean; yesLabel: string; noLabel: string; size?: 'xs' | 'sm' }> = ({
  value,
  yesLabel,
  noLabel,
  size,
}) => {
  const style = value ? { bg: 'success.100', text: 'success.700' } : { bg: 'gray.100', text: 'gray.600' };
  return <Badge bg={style.bg} text={style.text} label={value ? yesLabel : noLabel} size={size} />;
};

export const MutedText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text size="md" color="gray.400">
    {children}
  </Text>
);

// Row text. Primary (name): bold. Secondary cells: not bold.
// `danger` — red text for stale-date cells (Last Login, Last Scheduled, Employee
// Status Updated) once past the 30-day drift threshold. Single red tier only,
// no yellow/warning tier.
export const RowText: React.FC<{ children: React.ReactNode; bold?: boolean; danger?: boolean }> = ({
  children,
  bold = false,
  danger = false,
}) => (
  <Text size="md" fontWeight={bold ? 500 : 400} color={danger ? 'error.600' : bold ? 'gray.900' : 'gray.700'} numberOfLines={1}>
    {children}
  </Text>
);
